import React from "react";
import { motion } from "framer-motion";

// Clean static metadata matrix including all 11 search-engine-optimized blogs
const STATIC_BLOGS = [
  {
    category: "Market Directory Review",
    title: "Top 3 Authentic Ube Powder Suppliers & Distributors in India (2026 Review)",
    desc: "A vetted guide evaluating the top commercial purple yam distribution houses serving the Indian market, outranking generic aggregators.",
    url: "/blog/best-ube-suppliers-india.html",
    delay: 0.05
  },
  {
    category: "Supply Chain & Sourcing",
    title: "Sourcing Authentic Ube: Why Philippine Quality Matters for Indian Food Manufacturers",
    desc: "An insider look at farm-to-factory logistics of genuine purple yam managed cleanly to bypass common open marketplace quality dilution.",
    url: "/blog/sourcing-philippine-ube.html",
    delay: 0.1
  },
  {
    category: "Clean-Label Formulation",
    title: "Why Commercial Bakeries Are Replacing Artificial Colors with Wholesale Purple Yam",
    desc: "See how modern food processing plants eliminate synthetic chemical dyes using high-stability natural pigments from the Philippines.",
    url: "/blog/clean-label-purple-yam.html",
    delay: 0.15
  },
  {
    category: "Technical Formulation Guide",
    title: "The Formulation Guide: Integrating Pure Ube Liquid and Powder in Food Production",
    desc: "A technical evaluation for food scientists on utilizing bulk purple yam powder versus liquid extracts in high-throughput automated lines.",
    url: "/blog/ube-powder-vs-liquid-guide.html",
    delay: 0.2
  },
  {
    category: "Educational Guide",
    title: "What is Ube? The Complete Guide to the Filipino Purple Yam",
    desc: "Explore the botanical parameters of Dioscorea alata, its rich nutritional composition, and what makes it the absolute gold standard.",
    url: "/blog/what-is-ube.html",
    delay: 0.25
  },
  {
    category: "Ingredient Comparison",
    title: "Ube vs. Taro: Why Food Businesses Shouldn't Confuse the Two Ingredients",
    desc: "Understand the clear botanical and visual structural differences to protect recipe integrity and preserve clean-label consumer trust.",
    url: "/blog/ube-vs-taro.html",
    delay: 0.3
  },
  {
    category: "Market Trends",
    title: "Why Ube is the Viral Food Trend Sweeping India's Gourmet Culinary Market",
    desc: "Capitalize on the visual purple food movement taking over high-end metropolitan cafe structures, dessert bars, and artisanal gelatos.",
    url: "/blog/viral-ube-trend.html",
    delay: 0.35
  },
  {
    category: "Direct Trade Frameworks",
    title: "Sourcing Authentic Philippine Ube for Local Indian Confectionery Operations",
    desc: "How boutique kitchens scale production lines safely using direct maritime cargo freight without procurement friction.",
    url: "/blog/sourcing-philippine-ube-india.html",
    delay: 0.4
  },
  {
    category: "Commercial Guides",
    title: "How to Use Ube Powder: Complete Guide for Cafes and Bakeries",
    desc: "Master professional rehydration rules, storage guidelines, and high-volume menu scalability methods using the exact 1:3 ratio setup.",
    url: "/blog/how-to-use-ube-powder/index.html",
    delay: 0.45
  },
  {
    category: "Formulation Ratios",
    title: "How Much Ube Powder to Use in Recipes: Commercial Scaling Chart",
    desc: "Avoid flat, pale, or bitter batches. Access definitive percentage-based calculation matrices for professional cafe bar setups and yeast dough lines.",
    url: "/blog/how-much-ube-powder-to-use/index.html",
    delay: 0.5
  },
  {
    category: "Cafe Prep Manual",
    title: "How to Make High-Yield Ube Syrup from Premium Ube Powder",
    desc: "Maximize morning speed-of-service. Create a stable, smooth house-made ube coffee and boba syrup that blends instantly without separating.",
    url: "/blog/how-to-make-ube-syrup-from-powder/index.html",
    delay: 0.55
  }
];

export default function Blogs() {
  return (
    <div className="bg-[#FCFAF8] min-h-screen" data-testid="blogs-page">
      {/* Structural Header Grid Element Section */}
      <section className="pt-16 pb-12 md:pt-24 md:pb-16 bg-gradient-to-b from-[#F3EFEA] to-[#FCFAF8]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 text-center">
          <div className="eyebrow mb-3">Stories & Insights</div>
          <h1 className="font-display text-5xl md:text-6xl text-[#1A1A1A] mb-5">
            Bulk Ube India — <em className="text-[#4B0082]">market intelligence.</em>
          </h1>
          <p className="text-lg text-[#555] max-w-2xl mx-auto">
            Wholesale guides, trend analysis, recipe playbooks, and HORECA case studies — written for India's most ambitious food founders.
          </p>
        </div>
      </section>

      {/* Programmatic Grid Layout Array Parsing */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {STATIC_BLOGS.map((item, index) => (
              <motion.article 
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: item.delay }}
                className="surface-card overflow-hidden flex flex-col bg-white p-6 rounded-xl border border-gray-100 shadow-sm justify-between hover:shadow-md transition-shadow"
              >
                <div>
                  <span className="text-xs font-bold text-[#4B0082] uppercase tracking-wide">
                    {item.category}
                  </span>
                  <h2 className="text-xl font-bold text-[#1A1A1A] mt-2 mb-3">
                    {item.title}
                  </h2>
                  <p className="text-[#555] text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
                <a 
                  href={item.url} 
                  className="inline-flex items-center text-[#4B0082] font-semibold text-sm mt-4 hover:underline"
                >
                  Read Article →
                </a>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
